import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock, Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button, Input, Label, Alert, AlertDescription } from '@repo/ui';

const passwordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'auth.passwordMinLength')
      .regex(/[A-Z]/, 'auth.passwordUppercase')
      .regex(/[a-z]/, 'auth.passwordLowercase')
      .regex(/[^A-Za-z0-9]/, 'auth.passwordSpecial'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'auth.passwordMismatch',
    path: ['confirmPassword'],
  });

type PasswordFormValues = z.infer<typeof passwordSchema>;

interface PasswordStepProps {
  email: string;
  otp: string;
  onBack: () => void;
  onSuccess: () => void;
}

export const PasswordStep: React.FC<PasswordStepProps> = ({ email, otp, onBack, onSuccess }) => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>({
    // @ts-ignore
    resolver: async (values) => {
      const result = await passwordSchema.safeParseAsync(values);
      if (result.success) return { values: result.data, errors: {} };
      const errors = result.error.issues.reduce((acc: any, issue, index) => {
        const fieldName = issue.path[0] as string;
        const fieldError = acc[fieldName];
        const messageKey = `${issue.code}-${index}`;

        if (fieldError) {
          fieldError.types = {
            ...(fieldError.types ?? { [fieldError.type]: fieldError.message }),
            [messageKey]: issue.message,
          };
          return acc;
        }

        acc[fieldName] = {
          type: issue.code,
          message: issue.message,
          types: { [messageKey]: issue.message },
        };
        return acc;
      }, {});
      return { values: {}, errors };
    },
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  useEffect(() => {
    if (confirmPassword) {
      void trigger('confirmPassword');
    }
  }, [confirmPassword, newPassword, trigger]);

  const newPasswordMessages = errors.newPassword
    ? Array.from(
        new Set(
          Object.values(
            (errors.newPassword as { types?: Record<string, string> }).types ?? {
              default: errors.newPassword.message as string,
            }
          )
        )
      )
    : [];

  const getMessage = (message?: string) => {
    if (!message) return '';
    const translated = t(message, { count: 8 });
    return translated === message ? message : translated;
  };

  const onSubmit = async (data: PasswordFormValues) => {
    setError(null);
    try {
      await resetPassword({ email, otp, newPassword: data.newPassword });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.failedToResetPassword'));
    }
  };

  return (
    <div className="w-full">
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-8 pl-0 hover:bg-transparent hover:text-foreground text-muted-foreground cursor-pointer"
      >
        <ArrowLeft size={18} className="ltr:mr-2 rtl:ml-2 rtl:rotate-180" />
        {t('auth.back')}
      </Button>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="newPassword" className="mb-1.5 block">
            {t('auth.newPassword')}
          </Label>
          <Input
            {...register('newPassword')}
            id="newPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••••"
            icon={<Lock className="h-4 w-4" />}
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            }
            error={!!errors.newPassword}
            className="h-11"
          />
          {newPasswordMessages.length > 0 && (
            <div className="text-red-500 mt-1.5 pl-1 space-y-2 fade-in slide-in-from-top-1 duration-200">
              {newPasswordMessages.map((message) => (
                <div key={message} className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <p className="text-sm font-medium">{getMessage(message)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Label htmlFor="confirmPassword" className="mb-1.5 block">
            {t('auth.confirmPassword')}
          </Label>
          <Input
            {...register('confirmPassword')}
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="••••••••••"
            icon={<Lock className="h-4 w-4" />}
            rightElement={
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            }
            error={!!errors.confirmPassword}
            className="h-11"
          />
          {errors.confirmPassword && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">
                {getMessage(errors.confirmPassword.message as string)}
              </p>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full p bg-[oklch(0.34_0.17_262.91)] hover:bg-[oklch(0.34_0.17_262.91)]/90 text-white shadow-lg shadow-[oklch(0.34_0.17_262.91)]/20 -mt-3 cursor-pointer"
          size="sm"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mx-2" />
              {t('auth.resetting')}
            </>
          ) : (
            t('auth.resetPassword')
          )}
        </Button>
      </form>
    </div>
  );
};
